import {
  NUMEROLOGY_81_CHART_LUCK,
  NUMEROLOGY_81_GENDER_LUCK,
  NUMEROLOGY_81_META,
} from '../data/numerology81Full';
import type {
  FortuneStage,
  GridKey,
  GridResult,
  SancaiLiugeResult,
  Wuxing,
} from './sancai-liuge';

export const PROFESSIONAL_NAME_STUDY_FEATURE_ID = 'professionalNameStudy';
export const PROFESSIONAL_NAME_STUDY_UNLOCK_ID = 'pro:namestudy';
export const PROFESSIONAL_NAME_STUDY_TIME_ESTIMATE = '60-75秒';
export const PROFESSIONAL_NAME_STUDY_PROGRESS_STEPS = [
  '校验姓名资料与笔画结构',
  '抽取六格数理与三才主线',
  '结合性别与年龄生成专属解读',
  '推演阶段运势与健康提示',
  '整理专业姓名学报告',
] as const;
export const PROFESSIONAL_NAME_STUDY_DEMO_AGE = 32;
export type ProfessionalNameStudyMode = 'demo' | 'live';

export const PROFESSIONAL_NAME_STUDY_DEMO_REPORT_RAW = `您指的是**解读和分析人类行为及命运的常见技术**。这些方法往往通过观察、推理、心理暗示或符号象征来提供个人化的信息。

我现在将根据您的要求，在“六格数理深度解析”的“现代解读”部分，在保持凶吉断语清晰的基础上，稍微增加一两句这种解读技巧，让分析更具代入感。

#### **【姓名】**
张一二 (Zhāng Yī Èr)

#### **一、六格数理基础计算**
**1. 单字笔画标注**：
- 姓：张 = 張 = 11 画
- 名 1：一 = 一 = 1 画
- 名 2：二 = 二 = 2 画
**2. 六格数理公式推导**：
- 天格：11 (張) + 1 = 12
- 人格：11 (張) + 1 (一) = 12
- 地格：1 (一) + 2 (二) = 3
- 外格：2 (二) + 1 = 3
- 总格：11 (張) + 1 (一) + 2 (二) = 14
- 变格：11 (張) + 2 (二) = 13

#### **二、六格数理深度解析（每格必含 4 要素）**

-   **【天格：12】 掘井无泉 (凶)**
    *   \`数理含义\`：有无理伸张之象。不顾薄弱无力，企图做无能力做的事，反致失败。遇事易生不足之心。家庭缘薄，孤独，遇难，逆境、病弱、不如意、困难等。又可因其他运的配合而导致意外的失败，甚至有不能完寿的悲运。
    *   \`古文释义\`：天格主父母祖上之荫。此数如掘井不获甘泉，根基不稳，祖荫稀薄。其势如舟行浅滩，多有搁浅之虞，恐承袭无力，易感困顿。
    *   \`现代解读\`：天格代表父母、长辈及先天运势。此12数“掘井无泉”为凶，预示命主在人生早期，来自家庭或长辈的助力将极为有限，甚至可能面临根基不稳、支持不足的困境。在学业和事业起步阶段，将遭遇诸多阻碍，需独自面对挑战，难以获得有效的庇护。 **您可能很早就学会了独立，但也常常感到，某些重要的支持总是差那么一点，让您不得不自己去摸索。**

-   **【人格：12】 掘井无泉 (凶)**
    *   \`数理含义\`：有无理伸张之象。不顾薄弱无力，企图做无能力做的事，反致失败。遇事易生不足之心。家庭缘薄，孤独，遇难、逆境、病弱、不如意、困难等。又可因其他运的配合而导致意外的失败，甚至有不能完寿的悲运。
    *   \`古文释义\`：人格乃先天主运，主自身之气象。此数如掘井不获甘泉，根基不稳，主运易感困顿。人生旅途或需付出更多努力以克服内在与外在的阻碍。
    *   \`现代解读\`：人格是命主自身的核心运势。此12数“掘井无泉”为凶，表明命主在个人发展上将遭遇重重阻碍，即使付出巨大努力，也可能难以达到预期目标，常感事与愿违。意志薄弱，容易半途而废，缺乏贯彻始终的毅力。在人际关系方面，亲情缘薄，易感孤独，难以获得真正的理解与支持。 **您内心深处或许藏着一种不被理解的孤独感，有时会觉得自己一直在努力付出，但结果却总是差强人意，甚至会怀疑自己的选择是否正确。**

-   **【地格：3】 祥瑞 (吉)**
    *   \`数理含义\`：阴阳抱合，万物确定形成之象。有祥瑞福禄的暗示，为成功发达之兆。智达明敏，艺精工巧，备首领之资，享自然之福。名利两全，能成大事大业。荣进有望，福祉无穷。
    *   \`古文释义\`：地格为子女晚辈及前运之基。此数如春日祥瑞，万物萌发，预示早年运势亨通，晚辈有望，能为人生奠定良好基础。
    *   \`现代解读\`：地格掌管命主的早年运势（1-15岁）及与子女、下属、同事的关系。此3数“祥瑞”为吉，预示命主在早年时期将享有福禄，运势亨通。与晚辈、下属关系和睦，能够获得他们的支持与爱戴，为人生奠定坚实的基础。 **您天生有一种亲和力，尤其在与比自己年轻的人相处时，总能得到他们的喜爱和信任，您也很乐意去引导和帮助他们。**

-   **【外格：3】 祥瑞 (吉)**
    *   \`数理含义\`：阴阳抱合，万物确定形成之象。有祥瑞福禄的暗示，为成功发达之兆。智达明敏，艺精工巧，备首领之资，享自然之福。名利两全，能成大事大业。荣进有望，福祉无穷。
    *   \`古文释义\`：外格主交友、社交及外界环境。此数如春风化雨，广结善缘，预示人际关系和谐，外部环境良好，能得贵人相助。
    *   \`现代解读\`：外格代表命主的社交、人际关系和外部环境。此3数“祥瑞”为吉，意味着命主在社会交往中将广结善缘，人际关系和睦，容易得到贵人相助。外部环境对其发展极为有利，能够获得广泛的支持。 **您在人际交往中往往显得随和，很容易与人建立良好的关系，总能在需要时遇到伸出援手的朋友或同事。**

-   **【总格：14】 破兆 (凶)**
    *   \`数理含义\`：多破兆，家属缘薄。丧亲、失子、兄弟姊妹分离。孤独、不如意、烦闷、危难、遇厄、浮沉不定、为人慷慨、施恩招怨。劳而无功、辛惨孤独。其他运数配合不宜者，有伤天寿。
    *   \`古文释义\`：总格乃人生晚运之归宿。此数如秋风萧瑟，多有破败之兆。晚景或有不如意，劳而无功，需提前规划，以求安稳。
    *   \`现代解读\`：总格主老年运势（48-63岁后）及一生总体的结局。此14数“破兆”为凶，预示命主在人生的后半段将面临诸多意想不到的波折和失败。一生辛劳付出，最终可能劳而无功，甚至有家属缘薄、孤独烦闷之虞，晚景凄凉。 **您或许常常在思考人生的意义，对未来有深切的担忧，总觉得辛苦努力后，结果却不尽如人意，甚至会有施恩反遭怨的经历。**

-   **【变格：13】 春日牡丹 (吉)**
    *   \`数理含义\`：富学艺才能，有智谋奇略。忍柔当事，任何难事，皆巧于措置以奏大功。为得享富贵幸福的好诱导。充满智慧是其特点。
    *   \`古文释义\`：变格乃后天努力之结果。此数如春日牡丹，花开富贵，预示通过努力可得智略成功，才华出众，成就一番事业。
    *   \`现代解读\`：变格代表命主后天努力的结果。此13数“春日牡丹”为吉，意味着命主天资聪颖，富学艺才能，具备智谋奇略。通过自身的勤奋和智慧，能够克服重重难关，取得显著的成就，获得名利双收。 **您是一个善于学习和思考的人，总能凭借自己的聪明才智，在困境中找到突破口，甚至在别人看来棘手的问题，您也能巧妙化解。**

#### **三、六运联动**

| **运程** | **年龄** | **决定因素** | **五行关系** | **解读** |
| :------- | :------- | :----------- | :----------- | :------- |
| **少年运** | 1-15 岁 | 天格 (12 木) + 地格 (3 火) | 木生火 | 少年时期，天格之木生助地格之火，此为相生之局。命主在青少年时期，可能得到长辈的关爱与支持，学业与生活相对顺利，为未来的发展打下积极基础。 |
| **青年运** | 12-27 岁 | 人格 (12 木) + 地格 (3 火) | 木生火 | 青年时期，人格之木生助地格之火，此为生旺之势。命主在青年阶段，能展现出活力与创造力，地格代表的早年积累和基础，为人格的自我发展提供了养分。在事业初期，可能得到一些支持，有施展抱负的机会。 |
| **壮年运** | 24-39 岁 | 人格 (12 木) + 天格 (12 木) | 木与木比和 | 壮年时期，人格与天格皆为木，形成比和之势。此阶段命主精力充沛，有冲劲。天格代表的先天因素或长辈经验，能为人格的奋斗提供间接助力。然而，比和也可能意味着有时会过于坚持己见，需要学会变通。 |
| **中年运** | 36-51 岁 | 人格 (12 木) + 外格 (3 火) | 木生火 | 中年时期，人格之木生助外格之火，此为相生之局。命主在此阶段，能够将个人能力与外部环境良好结合，人际关系和睦，易得贵人相助，事业发展有望获得更多机遇和支持。 |
| **老年运** | 48-63 岁 | 总格 (14 火) + 人格 (12 木) | 木生火 | 老年时期，人格之木生助总格之火，此为相生之局。命主晚年运势虽有总格14火的波折之象，但人格之木的生助，有助于其以积极心态应对挑战，保持活力。若能注重调养身心，晚年生活仍可望平稳。 |
| **家庭运** | 已婚      | 地格 (3 火) | 火属性      | 地格3火为“祥瑞”之吉数，火主热情、光明。此数在家庭运中，预示命主在婚姻或家庭生活中能够保持积极向上的氛围，家庭关系有望和睦融洽。与配偶、子女关系良好，能感受到家庭的温暖。 |

#### **四、字音字义**
**1. 字音韵律**：
- 声调搭配：张 (zhāng，阴平，1声) - 一 (yī，阴平，1声) - 二 (èr，去声，4声)。
- 音律意象：声调从阴平到去声，有起伏变化，但“一”和“二”的组合略显简单，缺乏音律的丰富性。

**2. 字义赏析**：
- **张**：本义为开弓，引申为展开、扩大、声势浩大。作为姓氏，常给人以大气、开阔之感。
- **一**：数字，表示开始、唯一、统一。
- **二**：数字，表示双数、成对。
- **姓名合义**：“张一二”字面意义直白，数字组合，给人一种简洁、明确的印象。但可能缺乏深远的文化内涵或诗意。

**3. 字义溯源**：
- **张**：甲骨文像一个人手持弓箭的样子，本义是把弦安在弓上，后泛指展开、扩大。
- **一**：甲骨文像一横，表示数字。
- **二**：甲骨文像两横，表示数字。

#### **五、健康问题**
**1. 五行问题**：
根据人格与天格、地格、变格的五行生克关系：
- 人格五行：12 -> 2 -> **木**
- 天格五行：12 -> 2 -> 木 (木与木比和)
- 地格五行：3 -> 3 -> 火 (木生火，相生)
- 变格五行：13 -> 3 -> 火 (木生火，相生)
- 外格五行：3 -> 3 -> 火 (木生火，相生)
- 总格五行：14 -> 4 -> 火 (木生火，相生)
核心五行：人格为木，其他格多为木火相生或比和，整体木火能量较强。

**2. 易患疾病**：
- **木旺问题**：人格木旺，可能导致肝胆功能过盛或失调，易患肝、胆、头、肩相关疾病，如偏头痛、眩晕、眼疾、情绪易怒等。
- **木生火过旺问题**：人格木生助地格、变格、外格、总格的火，可能导致火势过旺，易患心脏、小肠、额、齿相关疾病，如心悸、失眠、口舌生疮、高血压等。

**3. 调理建议**：
- **生活习惯**：保持情绪稳定，避免过度劳累和熬夜。适度运动，如散步、瑜伽等，有助于疏肝理气。
- **饮食调理**：多食绿色蔬菜，少食辛辣油腻。可适当食用滋阴降火之品，如莲子、百合等。
- **情绪管理**：学会放松，培养兴趣爱好，保持乐观心态，避免情绪大起大落。
- **定期检查**：关注肝胆、心血管健康，定期体检。

#### **六、姓名总评与人生建议**
**1. 格局定性**：
此姓名格局，天格与人格存在严重的凶数，总格亦有破败之兆，尽管地格、外格和变格为吉祥之数，且五行多呈现木火相生之势，整体能量活跃，但凶数的影响将贯穿命主一生，使其在人生初期、自身发展及晚年运势上遭遇诸多挑战。吉数虽能带来部分积极影响，但难以完全抵消凶数带来的负面冲击。

**2. 综合评分**：
**45/100分** (严重偏低，亟待改善)

**3. 核心特质**：
-   **先天根基薄弱，早期发展受限**：天格的凶数，预示命主在先天条件及早期成长环境中，将缺乏强有力的支持，导致起步艰难。
-   **个人发展阻碍重重，易劳而无功**：人格的凶数，决定了命主在自身发展过程中，将面临巨大的挑战和挫折，即使努力也难有实质性突破，易陷入困境。
-   **后天智慧与外部助力并存，但难以扭转全局**：变格的吉数表明命主具备一定才智，外格的吉数也预示着人际关系尚可，能获得一些外部支持，但这些积极因素恐难从根本上扭转天格、人格、总格的凶险格局。
-   **晚年运势堪忧，结局可能不尽如人意**：总格的凶数，暗示命主一生辛劳后，晚年或将面临破败、孤独、失意，难以安享晚年。

**4. 改名建议：**
**此姓名中的凶数对您的命运影响深远，如同暗礁潜伏于人生航道，若不及时调整，恐将面临难以预料的巨大困境与损失。我们强烈建议您认真考虑改名，以期扭转乾坤，重塑吉祥人生。**

-   **天格（12 凶）的潜在影响与优化建议**：
    *   **问题所在**：天格12为“掘井无泉”之凶数，这直接预示着您的**家族根基与早期发展**将极为薄弱。您在学业、事业起步阶段，将缺乏来自长辈或家族的有力支持，如同在沙漠中掘井却无甘泉，一切都需从零开始，独自承担重压。这不仅会造成您早年学业或事业上的诸多不顺，更可能导致您在寻求外部资源时屡屡碰壁，使您的人生起点远低于常人，步履维艰。
    *   **优化方向**：为了彻底改变这种先天不足的困境，获得长辈荫庇与早期发展助力，我们强烈建议将天格调整为**吉祥的数理**。这能够为您的人生奠定稳固的基石，让您在起步阶段便能得到有利的引导与支持，为未来的发展积蓄力量。

-   **人格（12 凶）的潜在影响与优化建议**：
    *   **问题所在**：人格12同样为“掘井无泉”之凶数，作为您**个人命运的核心**，其影响最为致命。它预示着您将一生坎坷，即使付出百倍努力，也可能遭遇“劳而无功”的悲剧。在**事业发展**上，您可能怀才不遇，晋升困难，财运不济，甚至面临多次失败与破产的风险。在**健康方面**，此数易导致意志薄弱，身心疲惫，甚至诱发慢性疾病或意外伤病，严重影响生命质量。在**情感关系**上，此数意味着亲情缘薄，易感孤独，难以获得真挚的感情与理解，婚姻家庭恐多有不顺，甚至有离异之苦。
    *   **优化方向**：为了彻底扭转个人命运的颓势，提升事业成功率，改善健康状况，并获得幸福美满的情感生活，我们**必须**将人格调整为**吉祥的数理**。这能够增强您的意志力与行动力，使您在面对挑战时更有韧性，更能把握机遇，从而在事业、财富、健康和情感上获得全面的积极提升。

-   **总格（14 凶）的潜在影响与优化建议**：
    *   **问题所在**：总格14为“破兆”之凶数，作为您**晚年及一生总体结局**的象征，其影响令人担忧。它预示着您在经历了早中年的奋斗与磨砺后，晚年将面临极大的波折与破败。这可能导致您在**财富积累**上功亏一篑，晚年生活困顿不安，甚至有倾家荡产的风险。在**家庭关系**上，此数意味着家属缘薄，子女缘浅，晚年恐将孤独无依，感受不到天伦之乐。您一生的努力，最终可能化为泡影，晚景凄凉。
    *   **优化方向**：为了避免晚年凄惨的结局，确保一生功德圆满，安享幸福晚年，我们建议将总格调整为**吉祥的数理**。这能够为您的人生画上一个圆满的句号，使您在晚年时期获得稳定富足的生活，家庭和睦，子女孝顺，颐养天年。

综上所述，您当前的姓名中凶数密集，对您的命运构成了严重的负面影响。这并非小事，而是关系到您一生健康、财运、情感和事业兴衰的根本问题。为了避免这些不幸的发生，我们强烈建议您立即行动，寻求专业的姓名学指导，结合您的生辰八字和个人需求，选择一个数理更为吉祥、五行更为平衡、音形义俱佳的新名字。这将是您为自己人生做出的最明智、最重要的决定，能够彻底改变您的命运轨迹，让您的人生充满光明与希望。`;

export const PROFESSIONAL_NAME_STUDY_DEMO_REPORT: ProfessionalNameStudyReport = {
  heroSummary: '张一二这组姓名属于“底盘有顺势、主轴多负重”的结构：三才木火木有助起势，但人格14、变格19、总格22连续落在压力位，所以此名更像少年不差、成年后靠硬扛往前走的命格。若拿来做样例参考，它的价值不在“全吉”，而在于能把姓名学里最常见的顺逆交错、前后运落差、格局补救思路讲得非常完整。',
  currentFocus: '当前样例按32岁壮年阶段展开，重点观察事业承担、情绪消耗、关系边界和中后期转型承压能力。这个年龄段最怕的不是没机会，而是责任来得太快、心理恢复太慢，导致整个人长期处在“外面撑得住，里面很疲惫”的状态。',
  overview: [
    '张一二的三才组合为木火木，表面看属于上下有承接、起势不弱的配置，所以此名给人的第一印象往往不差：反应快、行动力够、脑子活、愿意出头，也容易在少年与青年阶段得到老师、长辈或环境的正向推动。',
    '但真正决定人生体感的不是三才表层，而是人格、变格、总格三条主轴。人格14主内在承压，变格19主中途波折，总格22主收束不稳，这就造成一种典型结构：起步不算差，真正难的是持续稳定、长期经营、情绪修复和后程收官。',
    '从专业姓名学角度看，这类名字最常见的现实映射，就是早年有冲劲，中段多劳心，越到承担责任的时候越容易出现“别人觉得你能扛，所以事情都往你身上压”的命运感。',
  ],
  gridAnalyses: [
    {
      key: 'tiange',
      label: '天格',
      number: 12,
      title: '掘井无泉',
      subtitle: '',
      genderLuck: '男女皆凶',
      luck: '凶',
      summary: '古文释义：天格主父母祖上之荫。此数如掘井不获甘泉，根基不稳，祖荫稀薄。其势如舟行浅滩，多有搁浅之虞，恐承袭无力，易感困顿。',
      base: '厄星、破厄、劫煞、劫财、时禄。',
      family: '亲情如秋水，骨肉似寒炭，施恩招怨恨，宜提高气节。',
      health: '枯木待春，宜小心神经衰弱，胃疾，外伤，皮肤病。三才善良可安全。',
      overall: '数理含义：有无理伸张之象。不顾薄弱无力，企图做无能力做的事，反致失败。遇事易生不足之心。家庭缘薄，孤独，遇难，逆境、病弱、不如意、困难等。又可因其他运的配合而导致意外的失败，甚至有不能完寿的悲运。',
      detailParagraphs: [
        '古文释义：天格主父母祖上之荫。此数如掘井不获甘泉，根基不稳，祖荫稀薄。其势如舟行浅滩，多有搁浅之虞，恐承袭无力，易感困顿。',
        '现代解读：天格代表父母、长辈及先天运势。此12数“掘井无泉”为凶，预示命主在人生早期，来自家庭或长辈的助力将极为有限，甚至可能面临根基不稳、支持不足的困境。在学业和事业起步阶段，将遭遇诸多阻碍，需独自面对挑战，难以获得有效的庇护。您可能很早就学会了独立，但也常常感到，某些重要的支持总是差那么一点，让您不得不自己去摸索。',
      ],
      meaning: '现代解读：天格代表父母、长辈及先天运势。此12数“掘井无泉”为凶，预示命主在人生早期，来自家庭或长辈的助力将极为有限，甚至可能面临根基不稳、支持不足的困境。在学业和事业起步阶段，将遭遇诸多阻碍，需独自面对挑战，难以获得有效的庇护。您可能很早就学会了独立，但也常常感到，某些重要的支持总是差那么一点，让您不得不自己去摸索。',
    },
    {
      key: 'renge',
      label: '人格',
      number: 12,
      title: '掘井无泉',
      subtitle: '',
      genderLuck: '男女皆凶',
      luck: '凶',
      summary: '古文释义：人格乃先天主运，主自身之气象。此数如掘井不获甘泉，根基不稳，主运易感困顿。人生旅途或需付出更多努力以克服内在与外在的阻碍。',
      base: '厄星、破厄、劫煞、劫财、时禄。',
      family: '亲情如秋水，骨肉似寒炭，施恩招怨恨，宜提高气节。',
      health: '枯木待春，宜小心神经衰弱，胃疾，外伤，皮肤病。三才善良可安全。',
      overall: '数理含义：有无理伸张之象。不顾薄弱无力，企图做无能力做的事，反致失败。遇事易生不足之心。家庭缘薄，孤独，遇难、逆境、病弱、不如意、困难等。又可因其他运的配合而导致意外的失败，甚至有不能完寿的悲运。',
      detailParagraphs: [
        '古文释义：人格乃先天主运，主自身之气象。此数如掘井不获甘泉，根基不稳，主运易感困顿。人生旅途或需付出更多努力以克服内在与外在的阻碍。',
        '现代解读：人格是命主自身的核心运势。此12数“掘井无泉”为凶，表明命主在个人发展上将遭遇重重阻碍，即使付出巨大努力，也可能难以达到预期目标，常感事与愿违。意志薄弱，容易半途而废，缺乏贯彻始终的毅力。在人际关系方面，亲情缘薄，易感孤独，难以获得真正的理解与支持。您内心深处或许藏着一种不被理解的孤独感，有时会觉得自己一直在努力付出，但结果却总是差强人意，甚至会怀疑自己的选择是否正确。',
      ],
      meaning: '现代解读：人格是命主自身的核心运势。此12数“掘井无泉”为凶，表明命主在个人发展上将遭遇重重阻碍，即使付出巨大努力，也可能难以达到预期目标，常感事与愿违。意志薄弱，容易半途而废，缺乏贯彻始终的毅力。在人际关系方面，亲情缘薄，易感孤独，难以获得真正的理解与支持。您内心深处或许藏着一种不被理解的孤独感，有时会觉得自己一直在努力付出，但结果却总是差强人意，甚至会怀疑自己的选择是否正确。',
    },
    {
      key: 'dige',
      label: '地格',
      number: 3,
      title: '祥瑞',
      subtitle: '',
      genderLuck: '男女皆吉',
      luck: '吉',
      summary: '古文释义：地格为子女晚辈及前运之基。此数如春日祥瑞，万物萌发，预示早年运势亨通，晚辈有望，能为人生奠定良好基础。',
      base: '学术、技艺、祖业、丰盛、自立、建业、官禄。',
      family: '家内施惠、可得贤妻、六亲和睦。',
      health: '松柏林立，健康良好，可得长寿。',
      overall: '数理含义：阴阳抱合，万物确定形成之象。有祥瑞福禄的暗示，为成功发达之兆。智达明敏，艺精工巧，备首领之资，享自然之福。名利两全，能成大事大业。荣进有望，福祉无穷。',
      detailParagraphs: [
        '古文释义：地格为子女晚辈及前运之基。此数如春日祥瑞，万物萌发，预示早年运势亨通，晚辈有望，能为人生奠定良好基础。',
        '现代解读：地格掌管命主的早年运势（1-15岁）及与子女、下属、同事的关系。此3数“祥瑞”为吉，预示命主在早年时期将享有福禄，运势亨通。与晚辈、下属关系和睦，能够获得他们的支持与爱戴，为人生奠定坚实的基础。您天生有一种亲和力，尤其在与比自己年轻的人相处时，总能得到他们的喜爱和信任，您也很乐意去引导和帮助他们。',
      ],
      meaning: '现代解读：地格掌管命主的早年运势（1-15岁）及与子女、下属、同事的关系。此3数“祥瑞”为吉，预示命主在早年时期将享有福禄，运势亨通。与晚辈、下属关系和睦，能够获得他们的支持与爱戴，为人生奠定坚实的基础。您天生有一种亲和力，尤其在与比自己年轻的人相处时，总能得到他们的喜爱和信任，您也很乐意去引导和帮助他们。',
    },
    {
      key: 'waige',
      label: '外格',
      number: 3,
      title: '祥瑞',
      subtitle: '',
      genderLuck: '男女皆吉',
      luck: '吉',
      summary: '古文释义：外格主交友、社交及外界环境。此数如春风化雨，广结善缘，预示人际关系和谐，外部环境良好，能得贵人相助。',
      base: '学术、技艺、祖业、丰盛、自立、建业、官禄。',
      family: '家内施惠、可得贤妻、六亲和睦。',
      health: '松柏林立，健康良好，可得长寿。',
      overall: '数理含义：阴阳抱合，万物确定形成之象。有祥瑞福禄的暗示，为成功发达之兆。智达明敏，艺精工巧，备首领之资，享自然之福。名利两全，能成大事大业。荣进有望，福祉无穷。',
      detailParagraphs: [
        '古文释义：外格主交友、社交及外界环境。此数如春风化雨，广结善缘，预示人际关系和谐，外部环境良好，能得贵人相助。',
        '现代解读：外格代表命主的社交、人际关系和外部环境。此3数“祥瑞”为吉，意味着命主在社会交往中将广结善缘，人际关系和睦，容易得到贵人相助。外部环境对其发展极为有利，能够获得广泛的支持。您在人际交往中往往显得随和，很容易与人建立良好的关系，总能在需要时遇到伸出援手的朋友或同事。',
      ],
      meaning: '现代解读：外格代表命主的社交、人际关系和外部环境。此3数“祥瑞”为吉，意味着命主在社会交往中将广结善缘，人际关系和睦，容易得到贵人相助。外部环境对其发展极为有利，能够获得广泛的支持。您在人际交往中往往显得随和，很容易与人建立良好的关系，总能在需要时遇到伸出援手的朋友或同事。',
    },
    {
      key: 'biange',
      label: '变格',
      number: 13,
      title: '春日牡丹',
      subtitle: '',
      genderLuck: '男女皆吉',
      luck: '吉',
      summary: '古文释义：变格乃后天努力之结果。此数如春日牡丹，花开富贵，预示通过努力可得智略成功，才华出众，成就一番事业。',
      base: '天官、文昌、技艺、进财、学士、田产、财库。',
      family: '祖宗余荫、子孙孝顺，可得团圆。',
      health: '可得健全长寿。',
      overall: '数理含义：富学艺才能，有智谋奇略。忍柔当事，任何难事，皆巧于措置以奏大功。为得享富贵幸福的好诱导。充满智慧是其特点。',
      detailParagraphs: [
        '古文释义：变格乃后天努力之结果。此数如春日牡丹，花开富贵，预示通过努力可得智略成功，才华出众，成就一番事业。',
        '现代解读：变格代表命主后天努力的结果。此13数“春日牡丹”为吉，意味着命主天资聪颖，富学艺才能，具备智谋奇略。通过自身的勤奋和智慧，能够克服重重难关，取得显著的成就，获得名利双收。您是一个善于学习和思考的人，总能凭借自己的聪明才智，在困境中找到突破口，甚至在别人看来棘手的问题，您也能巧妙化解。',
      ],
      meaning: '现代解读：变格代表命主后天努力的结果。此13数“春日牡丹”为吉，意味着命主天资聪颖，富学艺才能，具备智谋奇略。通过自身的勤奋和智慧，能够克服重重难关，取得显著的成就，获得名利双收。您是一个善于学习和思考的人，总能凭借自己的聪明才智，在困境中找到突破口，甚至在别人看来棘手的问题，您也能巧妙化解。',
    },
    {
      key: 'zongge',
      label: '总格',
      number: 14,
      title: '破兆',
      subtitle: '',
      genderLuck: '男女皆凶',
      luck: '凶',
      summary: '古文释义：总格乃人生晚运之归宿。此数如秋风萧瑟，多有破败之兆。晚景或有不如意，劳而无功，需提前规划，以求安稳。',
      base: '暗禄、美貌、艺术、流浪、红艳、劫财。',
      family: '骨肉疏远、离祖迁居，自家兄弟全无分，处处交友却有缘。',
      health: '男女均属好貌，皮肤病、感冒、风邪，先天五行相合者则健康。',
      overall: '数理含义：多破兆，家属缘薄。丧亲、失子、兄弟姊妹分离。孤独、不如意、烦闷、危难、遇厄、浮沉不定、为人慷慨、施恩招怨。劳而无功、辛惨孤独。其他运数配合不宜者，有伤天寿。',
      detailParagraphs: [
        '古文释义：总格乃人生晚运之归宿。此数如秋风萧瑟，多有破败之兆。晚景或有不如意，劳而无功，需提前规划，以求安稳。',
        '现代解读：总格主老年运势（48-63岁后）及一生总体的结局。此14数“破兆”为凶，预示命主在人生的后半段将面临诸多意想不到的波折和失败。一生辛劳付出，最终可能劳而无功，甚至有家属缘薄、孤独烦闷之虞，晚景凄凉。您或许常常在思考人生的意义，对未来有深切的担忧，总觉得辛苦努力后，结果却不尽如人意，甚至会有施恩反遭怨的经历。',
      ],
      meaning: '现代解读：总格主老年运势（48-63岁后）及一生总体的结局。此14数“破兆”为凶，预示命主在人生的后半段将面临诸多意想不到的波折和失败。一生辛劳付出，最终可能劳而无功，甚至有家属缘薄、孤独烦闷之虞，晚景凄凉。您或许常常在思考人生的意义，对未来有深切的担忧，总觉得辛苦努力后，结果却不尽如人意，甚至会有施恩反遭怨的经历。',
    },
  ],
  stageAnalyses: [
    { key: 'shaonian', label: '少年运', ageRange: '1-15岁', factors: '天格 (12 木) + 地格 (3 火)', wuxingRelation: '木生火', luck: '平', summary: '少年时期，天格之木生助地格之火，此为相生之局。命主在青少年时期，可能得到长辈的关爱与支持，学业与生活相对顺利，为未来的发展打下积极基础。' },
    { key: 'qingnian', label: '青年运', ageRange: '12-27岁', factors: '人格 (12 木) + 地格 (3 火)', wuxingRelation: '木生火', luck: '平', summary: '青年时期，人格之木生助地格之火，此为生旺之势。命主在青年阶段，能展现出活力与创造力，地格代表的早年积累和基础，为人格的自我发展提供了养分。在事业初期，可能得到一些支持，有施展抱负的机会。' },
    { key: 'zhuangnian', label: '壮年运', ageRange: '24-39岁', factors: '人格 (12 木) + 天格 (12 木)', wuxingRelation: '木与木比和', luck: '凶', summary: '壮年时期，人格与天格皆为木，形成比和之势。此阶段命主精力充沛，有冲劲。天格代表的先天因素或长辈经验，能为人格的奋斗提供间接助力。然而，比和也可能意味着有时会过于坚持己见，需要学会变通。' },
    { key: 'zhongnian', label: '中年运', ageRange: '36-51岁', factors: '人格 (12 木) + 外格 (3 火)', wuxingRelation: '木生火', luck: '平', summary: '中年时期，人格之木生助外格之火，此为相生之局。命主在此阶段，能够将个人能力与外部环境良好结合，人际关系和睦，易得贵人相助，事业发展有望获得更多机遇和支持。' },
    { key: 'laonian', label: '老年运', ageRange: '48-63岁', factors: '总格 (14 火) + 人格 (12 木)', wuxingRelation: '木生火', luck: '凶', summary: '老年时期，人格之木生助总格之火，此为相生之局。命主晚年运势虽有总格14火的波折之象，但人格之木的生助，有助于其以积极心态应对挑战，保持活力。若能注重调养身心，晚年生活仍可望平稳。' },
    { key: 'jiating', label: '家庭运', ageRange: '已婚', factors: '地格 (3 火)', wuxingRelation: '火属性', luck: '吉', summary: '地格3火为“祥瑞”之吉数，火主热情、光明。此数在家庭运中，预示命主在婚姻或家庭生活中能够保持积极向上的氛围，家庭关系有望和睦融洽。与配偶、子女关系良好，能感受到家庭的温暖。' },
  ],
  soundMeaning: [
    '1. 字音韵律：',
    '声调搭配：张 (zhāng，阴平，1声) - 一 (yī，阴平，1声) - 二 (èr，去声，4声)。',
    '音律意象：声调从阴平到去声，有起伏变化，但“一”和“二”的组合略显简单，缺乏音律的丰富性。',
    '2. 字义赏析：',
    '张：本义为开弓，引申为展开、扩大、声势浩大。作为姓氏，常给人以大气、开阔之感。',
    '一：数字，表示开始、唯一、统一。',
    '二：数字，表示双数、成对。',
    '姓名合义：“张一二”字面意义直白，数字组合，给人一种简洁、明确的印象。但可能缺乏深远的文化内涵或诗意。',
    '3. 字义溯源：',
    '张：甲骨文像一个人手持弓箭的样子，本义是把弦安在弓上，后泛指展开、扩大。',
    '一：甲骨文像一横，表示数字。',
    '二：甲骨文像两横，表示数字。',
  ],
  wuxingItems: [
    { element: '金', percentage: 0, summary: '金气当前不显。' },
    { element: '木', percentage: 33.3, summary: '人格与天格同属木，木与木比和。' },
    { element: '水', percentage: 0, summary: '水气当前不显。' },
    { element: '火', percentage: 66.7, summary: '地格、变格、外格、总格多落火位，木生火相生明显。' },
    { element: '土', percentage: 0, summary: '土气当前不显。' },
  ],
  wuxingOverview: '五行问题：根据人格与天格、地格、变格的五行生克关系：人格五行：木；天格五行：木（木与木比和）；地格五行：火（木生火，相生）；变格五行：火（木生火，相生）；外格五行：火（木生火，相生）；总格五行：火（木生火，相生）。核心五行：人格为木，其他格多为木火相生或比和，整体木火能量较强。',
  healthTips: [
    '易患疾病：',
    '木旺问题：人格木旺，可能导致肝胆功能过盛或失调，易患肝、胆、头、肩相关疾病，如偏头痛、眩晕、眼疾、情绪易怒等。',
    '木生火过旺问题：人格木生助地格、变格、外格、总格的火，可能导致火势过旺，易患心脏、小肠、额、齿相关疾病，如心悸、失眠、口舌生疮、高血压等。',
    '调理建议：',
    '生活习惯：保持情绪稳定，避免过度劳累和熬夜。适度运动，如散步、瑜伽等，有助于疏肝理气。',
    '饮食调理：多食绿色蔬菜，少食辛辣油腻。可适当食用滋阴降火之品，如莲子、百合等。',
    '情绪管理：学会放松，培养兴趣爱好，保持乐观心态，避免情绪大起大落。',
    '定期检查：关注肝胆、心血管健康，定期体检。',
  ],
  finalAdvice: [
    '1. 格局定性：此姓名格局，天格与人格存在严重的凶数，总格亦有破败之兆，尽管地格、外格和变格为吉祥之数，且五行多呈现木火相生之势，整体能量活跃，但凶数的影响将贯穿命主一生，使其在人生初期、自身发展及晚年运势上遭遇诸多挑战。吉数虽能带来部分积极影响，但难以完全抵消凶数带来的负面冲击。',
    '2. 综合评分：45/100分 (严重偏低，亟待改善)',
    '3. 核心特质：',
    '先天根基薄弱，早期发展受限：天格的凶数，预示命主在先天条件及早期成长环境中，将缺乏强有力的支持，导致起步艰难。',
    '个人发展阻碍重重，易劳而无功：人格的凶数，决定了命主在自身发展过程中，将面临巨大的挑战和挫折，即使努力也难有实质性突破，易陷入困境。',
    '后天智慧与外部助力并存，但难以扭转全局：变格的吉数表明命主具备一定才智，外格的吉数也预示着人际关系尚可，能获得一些外部支持，但这些积极因素恐难从根本上扭转天格、人格、总格的凶险格局。',
    '晚年运势堪忧，结局可能不尽如人意：总格的凶数，暗示命主一生辛劳后，晚年或将面临破败、孤独、失意，难以安享晚年。',
  ],
  renameAdvice: [
    '在对您的姓名磁场进行深度推演后，我感触最深的是一种“逆风执炬”的孤勇感。这种格局虽然磨炼意志，但作为陪伴一生的能量符号，它确实让您在追求圆满的道路上，承担了本不该有的沉重与波动。',
    '天格（12 凶）：关于根基与早期助力的观察',
    '天格12在数理上被称为“掘井无泉”，这预示着一种先天资源略显单薄的象义。在我的经验中，拥有这种数理的人，内心往往很早就明白“凡事只能靠自己”的道理。您可能在学业初期或事业萌芽阶段，就隐约感受到一种缺乏长辈提携、资源匮乏的冷清感。这种状态并非因为您不够努力，而是先天的基石缺少了一份生发的动力，导致您在人生起跑线上，往往需要付出数倍于常人的心力去挖掘那一口属于自己的甘泉。这种长期的独自承担，实际上是在过早地损耗您原本丰盈的生命底蕴。为了改善这种“白手起家”的吃力感，建议将其调整为吉祥数理，为您的人生引入更稳固的贵人加持与早期助力。',
    '人格（12 凶）：核心运势与身心平衡的解析',
    '人格作为命脉之所在，12数理在这里显现出一种典型的“劳而难获”的磁场。您是否常有一种感觉：明明机会就在眼前，却总在临门一脚时生出变故？或者在事业风生水起时，内心深处却总被一种莫名的孤独感与疲惫感占据。这种负向波动，极易让一个人的才华在周而复始的琐事中被磨损，甚至在情感与家庭中，也容易因沟通的错位而感到不被理解。长期处于这种能量场中，身心健康如同逆水行舟，那种由内而外的紧绷感，正是磁场不协调的警示。通过将人格调整为吉祥数理，核心目的在于激发内在潜能，转化阻力为动力，让您的每一分努力都能在事业、财富与情感中得到实实在在的回馈。',
    '总格（14 凶）：关于长远布局与晚景守护的审视',
    '总格14作为人生最终结局的象征，在数理中被称为“破兆”。这种能量带有极强的波动性，往往让一个人在中年拼搏之后，难以进入一种安稳守成的状态。您或许在夜深人静时也曾担忧过：如今辛劳积累的成果，未来是否能如愿稳固？这种象义容易在人生后期引发财富的流散或亲缘的淡薄，甚至让奋斗一生的硕果面临缩水的风险。为了规避这种“繁华散尽”的隐忧，确保一生功德圆满，我们建议将其调整为圆满、稳健的数理。这不仅是为了财富的稳固，更是为了在人生后期建立起深厚的家族纽带，确保您能拥有一种物质充盈、精神富足且亲情环绕的从容感。',
  ],
};

export interface ProfessionalNameStudyGridAnalysis {
  key: GridKey;
  label: string;
  number: number;
  title: string;
  subtitle: string;
  genderLuck: string;
  luck?: string;
  summary: string;
  base: string;
  family: string;
  health: string;
  overall?: string;
  detailParagraphs?: string[];
  meaning: string;
}

export interface ProfessionalNameStudyStageAnalysis {
  key: FortuneStage['key'];
  label: string;
  ageRange: string;
  factors?: string;
  wuxingRelation?: string;
  luck?: string;
  summary: string;
}

export interface ProfessionalNameStudyWuxingItem {
  element: Wuxing;
  percentage: number;
  summary: string;
}

export interface ProfessionalNameStudyReport {
  heroSummary: string;
  currentFocus: string;
  overview: string[];
  gridAnalyses: ProfessionalNameStudyGridAnalysis[];
  stageAnalyses: ProfessionalNameStudyStageAnalysis[];
  soundMeaning: string[];
  wuxingItems: ProfessionalNameStudyWuxingItem[];
  wuxingOverview: string;
  healthTips: string[];
  finalAdvice: string[];
  renameAdvice: string[];
}

const GRID_KEY_TO_FORMULA_LABEL: Record<GridKey, string> = {
  tiange: '天格',
  renge: '人格',
  dige: '地格',
  waige: '外格',
  biange: '变格',
  zongge: '总格',
};

const DETAIL_LABELS = [
  '寓意',
  '男女吉凶',
  '基业',
  '家庭',
  '健康',
  '含义（新版）',
  '含义（补充）',
  '含义',
  '含义和补充',
  '含义与补充',
  '补充核心',
] as const;
const DEFAULT_WUXING_SEQUENCE: Wuxing[] = ['金', '木', '水', '火', '土'];

const MEANING_LABEL_SET = new Set<string>([
  '含义（新版）',
  '含义（补充）',
  '含义',
  '含义和补充',
  '含义与补充',
  '补充核心',
  '整体',
]);

export const WUXING_COLOR_MAP: Record<Wuxing, string> = {
  金: '#c6922d',
  木: '#5f8f49',
  水: '#3f7ea8',
  火: '#cb5949',
  土: '#9a6b45',
};

export const toDisplayLuckTag = (tag: string): string => {
  const value = String(tag || '').replace(/\s+/g, '').trim();
  if (value === '吉带凶') return '半吉';
  if (value === '凶带吉') return '半凶';
  return value;
};

const normalizeChartLuckLabel = (raw: string): string => {
  const value = String(raw || '').replace(/\s+/g, '').trim().replace(/^男女吉凶[:：]?/, '');
  if (!value) return '';
  if (value.startsWith('男女皆') || value.startsWith('男女均')) {
    const tail = value.replace(/^男女[皆均]/, '').trim();
    return tail || value;
  }
  return value;
};

const trimTrailingPunctuation = (value: string): string => (
  String(value || '')
    .trim()
    .replace(/[。；;,.，、]+$/u, '')
);

const limitText = (value: string, max = 160): string => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(40, max - 1)).trim()}…`;
};

const safeString = (value: unknown, fallback = ''): string => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const normalizeCompareToken = (value: string): string => (
  String(value || '')
    .replace(/\s+/g, '')
    .replace(/[，。；;、,.!?！？:“”"'‘’（）()【】\[\]《》<>·\-—]/g, '')
    .trim()
);

const splitChineseSentences = (value: string): string[] => {
  const cleaned = String(value || '').replace(/\r/g, '').trim();
  if (!cleaned) return [];
  const lines = cleaned
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const source = lines.length > 0 ? lines.join('\n') : cleaned;
  const matches = source.match(/[^。！？!?；;\n]+[。！？!?；;]?/g) || [];
  const segments = matches
    .map((item) => item.trim())
    .filter(Boolean);
  return segments.length > 0 ? segments : [source];
};

const isNearDuplicateToken = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (a === b) return true;
  return a.includes(b) || b.includes(a);
};

const mergeMeaningTexts = (values: string[]): string => {
  const kept: string[] = [];
  const keptTokens: string[] = [];

  values.forEach((value) => {
    splitChineseSentences(value).forEach((sentence) => {
      const text = sentence.trim();
      if (!text) return;
      const token = normalizeCompareToken(text);
      if (!token) return;
      const duplicatedIndex = keptTokens.findIndex((item) => isNearDuplicateToken(item, token));
      if (duplicatedIndex >= 0) {
        if (text.length > kept[duplicatedIndex].length) {
          kept[duplicatedIndex] = text;
          keptTokens[duplicatedIndex] = token;
        }
        return;
      }
      kept.push(text);
      keptTokens.push(token);
    });
  });

  return kept.join('');
};

const formatLongTextToParagraphs = (value: string, maxCharsPerParagraph = 68): string => {
  const text = String(value || '').replace(/\r/g, '').trim();
  if (!text) return '';
  const manualParagraphs = text
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (manualParagraphs.length > 1) return manualParagraphs.join('\n');

  if (text.length <= maxCharsPerParagraph) return text;
  const sentences = splitChineseSentences(text);
  if (sentences.length <= 1) return text;

  const paragraphs: string[] = [];
  let current = '';
  sentences.forEach((sentence) => {
    if (!current) {
      current = sentence;
      return;
    }
    if ((current + sentence).length <= maxCharsPerParagraph) {
      current += sentence;
      return;
    }
    paragraphs.push(current);
    current = sentence;
  });
  if (current) paragraphs.push(current);
  return paragraphs.join('\n');
};

const normalizeDetailLabel = (raw: string): string => {
  const label = String(raw || '').trim();
  if (!label) return '';
  if (MEANING_LABEL_SET.has(label)) return '含义';
  return label;
};

const choosePreferredDetailValue = (current: string, candidate: string): string => {
  const currentValue = String(current || '').trim();
  const nextValue = String(candidate || '').trim();
  if (!currentValue) return nextValue;
  if (!nextValue) return currentValue;
  const currentToken = normalizeCompareToken(currentValue);
  const nextToken = normalizeCompareToken(nextValue);
  if (isNearDuplicateToken(currentToken, nextToken)) {
    return nextValue.length > currentValue.length ? nextValue : currentValue;
  }
  return currentValue.length >= nextValue.length ? currentValue : nextValue;
};

const normalizeParagraphArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item ?? '').trim())
    .filter(Boolean);
};

const toFinitePercentage = (value: unknown): number => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.max(0, Math.min(100, Number(num.toFixed(1))));
};

export const getShortGridLabel = (key: GridKey): string => {
  switch (key) {
    case 'tiange':
      return '天';
    case 'renge':
      return '人';
    case 'dige':
      return '地';
    case 'waige':
      return '外';
    case 'biange':
      return '变';
    case 'zongge':
      return '总';
    default:
      return '';
  }
};

export const parseFullTextToDetails = (fullText: string): { label: string; value: string }[] => {
  const parsedDetails: { label: string; value: string }[] = [];
  const parts = String(fullText || '').split(/\n\n+/);
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const titleMatch = trimmed.match(/^[○◎●△▲]?\d+[（(][^）)]+[）)][：:]\s*(.+)$/s);
    if (titleMatch && titleMatch[1]) {
      parsedDetails.push({ label: '寓意', value: titleMatch[1].trim() });
      continue;
    }

    let matched = false;
    for (const label of DETAIL_LABELS) {
      if (trimmed.startsWith(label + '：') || trimmed.startsWith(label + ':')) {
        const value = trimmed.slice(label.length + 1).trim();
        if (value) parsedDetails.push({ label, value });
        matched = true;
        break;
      }
    }
    if (matched) continue;

    if (!trimmed.includes('：') && !trimmed.includes(':')) continue;
    const generic = trimmed.match(/^([^：:]+)[：:]\s*(.*)$/s);
    if (generic && generic[2]) {
      const label = generic[1].trim();
      parsedDetails.push({
        label: label.length <= 8 ? label : '寓意',
        value: generic[2].trim(),
      });
    }
  }
  if (parsedDetails.length === 0 && String(fullText || '').trim()) {
    parsedDetails.push({ label: '含义', value: String(fullText || '').trim() });
  }

  const detailMap = new Map<string, string>();
  const meaningValues: string[] = [];

  parsedDetails.forEach((item) => {
    const label = normalizeDetailLabel(item.label);
    const value = String(item.value || '').trim();
    if (!label || !value) return;
    if (label === '含义') {
      meaningValues.push(value);
      return;
    }
    detailMap.set(label, choosePreferredDetailValue(detailMap.get(label) || '', value));
  });

  if (meaningValues.length > 0) {
    const mergedMeaning = formatLongTextToParagraphs(mergeMeaningTexts(meaningValues));
    if (mergedMeaning) detailMap.set('含义', mergedMeaning);
  }

  const orderedLabels = ['寓意', '男女吉凶', '基业', '家庭', '健康', '含义'];
  const normalized: { label: string; value: string }[] = [];
  orderedLabels.forEach((label) => {
    const value = String(detailMap.get(label) || '').trim();
    if (!value) return;
    normalized.push({ label, value });
    detailMap.delete(label);
  });

  detailMap.forEach((value, label) => {
    if (!value) return;
    normalized.push({ label, value });
  });

  return normalized;
};

const getDetailValue = (grid: GridResult, label: string): string => (
  parseFullTextToDetails(grid.fullText81).find((item) => item.label === label)?.value || ''
);

const getCombinedMeaningValue = (grid: GridResult): string => {
  const mergedMeaning = getDetailValue(grid, '含义');
  if (mergedMeaning) return mergedMeaning;
  const meaningNew = getDetailValue(grid, '含义（新版）');
  const meaningExtra = getDetailValue(grid, '含义（补充）');
  const legacyMeaning = getDetailValue(grid, '含义');
  const merged = mergeMeaningTexts([meaningNew, meaningExtra, legacyMeaning].filter(Boolean));
  const fallback = merged || grid.meaning81;
  return formatLongTextToParagraphs(fallback);
};

export const getGridNumerologyTitle = (grid: GridResult): string => (
  safeString(NUMEROLOGY_81_META[grid.value81]?.title, safeString(grid.meaning81))
);

export const getGridNumerologySubtitle = (grid: GridResult): string => (
  trimTrailingPunctuation(safeString(NUMEROLOGY_81_META[grid.value81]?.subtitle, safeString(grid.meaning81)))
);

export const getGridGenderLuckText = (grid: GridResult): string => {
  const fromMeta = safeString(NUMEROLOGY_81_META[grid.value81]?.genderLuck);
  if (fromMeta) return fromMeta;
  const fromMap = safeString(NUMEROLOGY_81_GENDER_LUCK[grid.value81]);
  if (fromMap) return fromMap;
  const displayTag = toDisplayLuckTag(grid.luckTag81 || grid.luckLevel);
  if (displayTag === '半吉' || displayTag === '半凶' || displayTag === '半吉半凶') {
    return '男女参半';
  }
  if (displayTag.includes('凶') && !displayTag.includes('吉')) return '男女皆凶';
  return '男女皆吉';
};

export const getGridChartLuckText = (grid: GridResult): string => {
  const fromMeta = normalizeChartLuckLabel(NUMEROLOGY_81_META[grid.value81]?.chartLuck || '');
  if (fromMeta) return fromMeta;
  const fromMap = normalizeChartLuckLabel(NUMEROLOGY_81_CHART_LUCK[grid.value81] || '');
  if (fromMap) return fromMap;
  const displayTag = toDisplayLuckTag(grid.luckTag81 || grid.luckLevel);
  return normalizeChartLuckLabel(displayTag || '平') || '平';
};

export const getSimpleGridHeader = (grid: GridResult): string => {
  const title = trimTrailingPunctuation(getGridNumerologyTitle(grid));
  const subtitle = trimTrailingPunctuation(getGridNumerologySubtitle(grid));
  const genderLuck = getGridGenderLuckText(grid);
  if (title && subtitle) {
    return `（${grid.value81}）${title}：${subtitle}（${genderLuck}）`;
  }
  if (title) {
    return `（${grid.value81}）${title}（${genderLuck}）`;
  }
  return `（${grid.value81}）${trimTrailingPunctuation(grid.meaning81)}（${genderLuck}）`;
};

export const getAgeFocusLabel = (age: number | null | undefined): string => {
  const safeAge = Number(age);
  if (!Number.isFinite(safeAge) || safeAge <= 0) return '未提供年龄，按通用阶段综合判断';
  if (safeAge <= 12) return '童少阶段';
  if (safeAge <= 18) return '少年阶段';
  if (safeAge <= 28) return '青年阶段';
  if (safeAge <= 40) return '壮年阶段';
  if (safeAge <= 55) return '中年阶段';
  return '晚年阶段';
};

export const getWuxingDistribution = (result: SancaiLiugeResult): ProfessionalNameStudyWuxingItem[] => {
  const ordered = (['tiange', 'renge', 'dige', 'waige', 'biange', 'zongge'] as const).map((key) => result.grids[key]);
  const total = ordered.length || 1;
  const countMap = ordered.reduce<Record<string, number>>((acc, grid) => {
    acc[grid.wuxing] = (acc[grid.wuxing] || 0) + 1;
    return acc;
  }, {});

  return DEFAULT_WUXING_SEQUENCE.map((element) => {
    const count = countMap[element] || 0;
    const percentage = Number(((count / total) * 100).toFixed(1));
    const summary = count === 0
      ? `${element}气偏弱，后天更需要借作息、环境与行为节奏去补足。`
      : count >= 2
        ? `${element}气偏旺，容易把对应特质放大，宜注意平衡。`
        : `${element}气处于中段，可作为辅助调性使用。`;
    return { element, percentage, summary };
  });
};

export const buildProfessionalNameStudyCacheKey = (
  result: SancaiLiugeResult,
  age: number | null | undefined,
  mode: ProfessionalNameStudyMode,
): string => JSON.stringify({
  mode,
  fullName: result.fullName,
  gender: result.gender,
  age: Number.isFinite(Number(age)) ? Number(age) : null,
  grids: (['tiange', 'renge', 'dige', 'waige', 'biange', 'zongge'] as const).map((key) => result.grids[key].value81),
  threeTalents: result.threeTalents.sequence,
});

export const buildProfessionalNameStudyFallback = (
  result: SancaiLiugeResult,
  age: number | null | undefined,
): ProfessionalNameStudyReport => {
  const ordered = (['tiange', 'renge', 'dige', 'waige', 'biange', 'zongge'] as const).map((key) => result.grids[key]);
  const focusLabel = getAgeFocusLabel(age);
  const genderLabel = result.gender === 'female' ? '女性' : '男性';
  const wuxingItems = getWuxingDistribution(result);
  const diseaseText = result.diseaseRisks.length > 0
    ? result.diseaseRisks.map((risk) => `${risk.rule}，重点留意${risk.organs}。`).join(' ')
    : '当前姓名结构未见强烈相克，健康层面重在守作息与情绪稳定。';

  return {
    heroSummary: `${result.fullName}这组姓名以${result.grids.renge.label}${result.grids.renge.value81}与${result.grids.zongge.label}${result.grids.zongge.value81}为主轴，整体呈现${result.threeTalents.sequence}三才格局，解读时需要同时看${genderLabel}身份与${focusLabel}的现实处境。`,
    currentFocus: `当前按${focusLabel}为重点观察口径，专业版会优先解释这一年龄段最容易放大的机会、压力与关系课题。`,
    overview: [
      `${result.threeTalents.summary} 这意味着姓名气场不是单点判断，而是六格结构共同决定你的主运节奏、外界反馈和中后期走势。`,
      `${result.grids.renge.label}${result.grids.renge.value81}是核心主轴，决定做事方式与关键决策风格；${result.grids.biange.label}${result.grids.biange.value81}、${result.grids.zongge.label}${result.grids.zongge.value81}则更能看出中晚期转型与收官能力。`,
    ],
    gridAnalyses: ordered.map((grid) => ({
      key: grid.key,
      label: grid.label,
      number: grid.value81,
      title: getGridNumerologyTitle(grid),
      subtitle: getGridNumerologySubtitle(grid),
      genderLuck: getGridGenderLuckText(grid),
      summary: `${grid.label}${grid.value81}属${grid.wuxing}${grid.yinYang}，当前主调为${getGridChartLuckText(grid)}。${grid.meaning81} 现实里更像是你在对应领域的做事习惯与能量习性。`,
      base: getDetailValue(grid, '基业'),
      family: getDetailValue(grid, '家庭'),
      health: getDetailValue(grid, '健康'),
      meaning: getCombinedMeaningValue(grid),
    })),
    stageAnalyses: result.stages.map((stage) => ({
      key: stage.key,
      label: stage.label,
      ageRange: stage.ageRange,
      summary: `${stage.summary} 再结合${stage.factors}来看，这一运最重要的是先稳结构，再放动作，避免在节奏未定时急推大事。`,
    })),
    soundMeaning: [
      `${result.surname}${result.givenName}在字面气质上会先给人留下${result.grids.renge.wuxing}性特质：讲秩序、重表达、在意外界反馈。`,
      `专业姓名学不仅看笔画，还会看字音、字形、字义是否与六格主线一致；如果字义很轻、数理很重，就容易出现外在印象与实际运势不同步。`,
    ],
    wuxingItems,
    wuxingOverview: `五行分布以六格合计为准。${wuxingItems.map((item) => `${item.element}${item.percentage}%`).join('，')}。${diseaseText}`,
    healthTips: result.diseaseRisks.length > 0
      ? result.diseaseRisks.map((risk) => `${risk.rule}时，通常先从疲劳、睡眠、情绪或对应脏腑负担上给信号，需尽早体检核实。`)
      : [
          '当前结构以日常调养为主，重点守住睡眠、饮食和持续运动，避免把轻度失衡拖成慢性问题。',
          '若现实中已出现持续不适，应以正规医疗检查结论为准，姓名学只作趋势提醒。',
        ],
    finalAdvice: [
      `${result.fullName}这组名字更适合用长期主义去经营，不宜走情绪化、忽强忽弱的推进方式。`,
      `如果你正处于${focusLabel}的压力阶段，先稳住主业、边界与睡眠节律，往往比急着做大动作更有效。`,
      `真正的优化重点通常不在“追求全吉”，而在于让人格、变格、总格之间少冲突、多接力。`,
    ],
    renameAdvice: [
      '若后续考虑改名，优先看人格、变格、总格是否存在明显断层，再决定是微调用字还是整体换名。',
      '改名方案要同时兼顾数理、音形义、性别气质和年龄阶段需求，不能只盯单个高分数字。',
    ],
  };
};

const cleanJsonText = (raw: string): string => (
  String(raw || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim()
);

const parseJsonLoose = (raw: string): unknown => {
  const cleaned = cleanJsonText(raw);
  if (!cleaned) return null;
  try {
    return JSON.parse(cleaned);
  } catch {
    const start = cleaned.indexOf('{');
    const end = cleaned.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(cleaned.slice(start, end + 1));
      } catch {
        return null;
      }
    }
    return null;
  }
};

export const parseProfessionalNameStudyReport = (
  rawText: string,
  fallback: ProfessionalNameStudyReport,
): ProfessionalNameStudyReport => {
  const parsed = parseJsonLoose(rawText);
  if (!parsed || typeof parsed !== 'object') return fallback;
  const source = parsed as Record<string, unknown>;

  const fallbackGridMap = new Map(fallback.gridAnalyses.map((item) => [item.key, item]));
  const fallbackStageMap = new Map(fallback.stageAnalyses.map((item) => [item.key, item]));

  const gridAnalyses = Array.isArray(source.gridAnalyses)
    ? source.gridAnalyses
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const row = item as Record<string, unknown>;
          const key = safeString(row.key) as GridKey;
          const base = fallbackGridMap.get(key);
          if (!base) return null;
          return {
            ...base,
            title: safeString(row.title, base.title),
            subtitle: safeString(row.subtitle, base.subtitle),
            genderLuck: safeString(row.genderLuck, base.genderLuck),
            summary: safeString(row.summary, base.summary),
            base: safeString(row.base, base.base),
            family: safeString(row.family, base.family),
            health: safeString(row.health, base.health),
            meaning: safeString(row.meaning, base.meaning),
          };
        })
        .filter((item): item is ProfessionalNameStudyGridAnalysis => Boolean(item))
    : [];

  const stageAnalyses = Array.isArray(source.stageAnalyses)
    ? source.stageAnalyses
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const row = item as Record<string, unknown>;
          const key = safeString(row.key) as FortuneStage['key'];
          const base = fallbackStageMap.get(key);
          if (!base) return null;
          return {
            ...base,
            summary: safeString(row.summary, base.summary),
          };
        })
        .filter((item): item is ProfessionalNameStudyStageAnalysis => Boolean(item))
    : [];

  const fallbackWuxingMap = new Map(fallback.wuxingItems.map((item) => [item.element, item]));
  const wuxingItems = Array.isArray(source.wuxingItems)
    ? source.wuxingItems
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const row = item as Record<string, unknown>;
          const element = safeString(row.element) as Wuxing;
          const base = fallbackWuxingMap.get(element);
          if (!base) return null;
          return {
            ...base,
            percentage: toFinitePercentage(row.percentage || base.percentage),
            summary: safeString(row.summary, base.summary),
          };
        })
        .filter((item): item is ProfessionalNameStudyWuxingItem => Boolean(item))
    : [];

  return {
    heroSummary: safeString(source.heroSummary, fallback.heroSummary),
    currentFocus: safeString(source.currentFocus, fallback.currentFocus),
    overview: normalizeParagraphArray(source.overview).slice(0, 4).length > 0
      ? normalizeParagraphArray(source.overview).slice(0, 4)
      : fallback.overview,
    gridAnalyses: gridAnalyses.length > 0 ? gridAnalyses : fallback.gridAnalyses,
    stageAnalyses: stageAnalyses.length > 0 ? stageAnalyses : fallback.stageAnalyses,
    soundMeaning: normalizeParagraphArray(source.soundMeaning).slice(0, 4).length > 0
      ? normalizeParagraphArray(source.soundMeaning).slice(0, 4)
      : fallback.soundMeaning,
    wuxingItems: wuxingItems.length > 0 ? wuxingItems : fallback.wuxingItems,
    wuxingOverview: safeString(source.wuxingOverview, fallback.wuxingOverview),
    healthTips: normalizeParagraphArray(source.healthTips).slice(0, 4).length > 0
      ? normalizeParagraphArray(source.healthTips).slice(0, 4)
      : fallback.healthTips,
    finalAdvice: normalizeParagraphArray(source.finalAdvice).slice(0, 5).length > 0
      ? normalizeParagraphArray(source.finalAdvice).slice(0, 5)
      : fallback.finalAdvice,
    renameAdvice: normalizeParagraphArray(source.renameAdvice).slice(0, 4).length > 0
      ? normalizeParagraphArray(source.renameAdvice).slice(0, 4)
      : fallback.renameAdvice,
  };
};

export const buildProfessionalNameStudyUserMessage = (
  result: SancaiLiugeResult,
  age: number | null | undefined,
  mode: ProfessionalNameStudyMode,
): string => {
  const ordered = (['tiange', 'renge', 'dige', 'waige', 'biange', 'zongge'] as const).map((key) => result.grids[key]);
  const strokeDetails = result.strokeSlots.map((slot) => ({
    char: slot.char,
    normalizedChar: slot.normalizedChar,
    part: slot.partLabel,
    stroke: slot.finalStroke,
    source: slot.source,
    wuxing: slot.charWuxing || '',
  }));
  const payload = {
    mode,
    requestLabel: mode === 'demo' ? '专业姓名学报告样例' : '专业姓名学实时报告',
    basicProfile: {
      fullName: result.fullName,
      surname: result.surname,
      givenName: result.givenName,
      gender: result.gender === 'female' ? '女' : '男',
      age: Number.isFinite(Number(age)) ? Number(age) : null,
      ageFocus: getAgeFocusLabel(age),
    },
    strokeDetails,
    threeTalents: result.threeTalents,
    grids: ordered.map((grid) => ({
      key: grid.key,
      label: grid.label,
      formulaLabel: GRID_KEY_TO_FORMULA_LABEL[grid.key],
      value81: grid.value81,
      wuxing: grid.wuxing,
      yinYang: grid.yinYang,
      luck: getGridChartLuckText(grid),
      numerologyTitle: getGridNumerologyTitle(grid),
      numerologySubtitle: getGridNumerologySubtitle(grid),
      genderLuck: getGridGenderLuckText(grid),
      meaning: limitText(grid.meaning81, 80),
      base: limitText(getDetailValue(grid, '基业'), 90),
      family: limitText(getDetailValue(grid, '家庭'), 90),
      health: limitText(getDetailValue(grid, '健康'), 90),
      detailMeaning: limitText(getCombinedMeaningValue(grid), 140),
    })),
    stages: result.stages.map((stage) => ({
      key: stage.key,
      label: stage.label,
      ageRange: stage.ageRange,
      factors: stage.factors,
      level: stage.level,
      summary: limitText(stage.summary, 120),
    })),
    diseaseRisks: result.diseaseRisks.map((risk) => ({
      ...risk,
      organs: limitText(risk.organs, 60),
    })),
    warnings: result.warnings.map((item) => limitText(item, 90)),
    wuxingItems: getWuxingDistribution(result),
    writingTargets: {
      preferredSections: ['姓名档案', '六格数理深度解析', '六运联动', '字音字义', '健康问题', '姓名总评与人生建议'],
      finalAdviceLayout: ['格局定性', '综合评分', '核心特质', '行动指南'],
    },
  };

  return [
    '请根据以下姓名学结构化数据生成专业姓名学报告。',
    '必须只返回系统提示词要求的 JSON，不要输出额外说明、不要加 Markdown 代码块。',
    '严禁原样回显输入 JSON，也不要把 basicProfile、threeTalents、grids、stages、diseaseRisks、warnings 这些输入字段直接当成最终输出顶层字段。',
    '最终输出的第一层字段只能是：heroSummary、currentFocus、overview、gridAnalyses、stageAnalyses、soundMeaning、wuxingItems、wuxingOverview、healthTips、finalAdvice、renameAdvice。',
    'gridAnalyses 六项必须写出“数理含义 + 古文释义 + 现代解读”的综合表达；finalAdvice 四段优先对应“格局定性、综合评分、核心特质、行动指南”。',
    '输入数据如下（仅供分析，不可照抄）：',
    '<input_json>',
    JSON.stringify(payload, null, 2),
    '</input_json>',
    '现在请直接输出最终报告 JSON。',
  ].join('\n\n');
};
